import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Experience.css';
import { experienceData } from '../../data/experienceData';
import { getDurationMonthsYears } from '../../data/schemas';
import { FaCalendarAlt, FaMapMarkerAlt, FaExternalLinkAlt, FaBriefcase, FaMicrophone } from 'react-icons/fa';

function formatDateLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[m - 1]} ${y}`;
}

function buildMilestones() {
  const items = [];
  experienceData.experiences.forEach((exp) => {
    items.push({ type: 'experience', sortDate: exp.startDate, ...exp });
  });
  (experienceData.conferences || []).forEach((conf) => {
    items.push({ type: 'conference', sortDate: conf.date, ...conf });
  });
  items.sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''));
  return items;
}

const Experience = () => {
  const [expandedId, setExpandedId] = useState(null);
  const milestones = useMemo(() => buildMilestones(), []);

  // Winding path: alternating left/right positions (percent) for a journey feel
  const getNodePosition = (index, total) => {
    const isLeft = index % 2 === 0;
    return { isLeft };
  };

  return (
    <section id={experienceData.header.id} className="experience-section journey-section">
      <motion.h5
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        {experienceData.header.subtitle}
      </motion.h5>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.08 }}
      >
        {experienceData.header.title}
      </motion.h2>

      <div className="container journey-container">
        <motion.div
          className="journey-summary"
          initial={{ opacity: 0, y: 32, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', damping: 26, stiffness: 120 }}
        >
          <h3>{experienceData.summary.title}</h3>
          <p>{experienceData.summary.content}</p>
          <p className="journey-context">All roles based in Palestine · Conferences across the region</p>
          <div className="journey-legend">
            <span className="legend-item"><span className="legend-shape work" /><FaBriefcase /> Work</span>
            <span className="legend-item"><span className="legend-shape conference" /><FaMicrophone /> Conferences</span>
          </div>
        </motion.div>

        <motion.div
          className="journey-path-wrap"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          {/* Stylized path line */}
          <div className="journey-path-bg" aria-hidden="true">
            <svg className="journey-path-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <linearGradient id="journeyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="var(--color-primary)" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.4" />
                </linearGradient>
              </defs>
              <path
                d="M 20 12 Q 50 12 80 28 Q 20 44 75 60 Q 25 76 80 92"
                fill="none"
                stroke="url(#journeyGradient)"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="journey-milestones">
            {milestones.map((node, index) => {
              const { isLeft } = getNodePosition(index, milestones.length);
              const isExp = node.type === 'experience';
              const label = isExp ? node.company : node.name;
              const subLabel = isExp ? node.title : `${formatDateLabel(node.date)} · ${node.location || ''}`;
              const isOpen = expandedId === index;

              return (
                <motion.div
                  key={index}
                  className={`journey-node ${isLeft ? 'left' : 'right'} ${isExp ? 'work' : 'conference'} ${isOpen ? 'open' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ delay: index * 0.06, type: 'spring', damping: 22, stiffness: 150 }}
                >
                  <button
                    type="button"
                    className="journey-milestone-btn"
                    onClick={() => setExpandedId(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? `Close ${label}` : `Open ${label}`}
                  >
                    <span className={`milestone-shape ${isExp ? 'work' : 'conference'}`}>
                      {isExp ? <FaBriefcase /> : <FaMicrophone />}
                    </span>
                    <span className="milestone-label">{label}</span>
                    <span className="milestone-sublabel">{subLabel}</span>
                    <span className="milestone-chevron">{isOpen ? '−' : '+'}</span>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        className="journey-detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      >
                        {isExp ? (
                          <>
                            <div className="detail-header">
                              <h4>{node.title}</h4>
                              <p className="detail-company">{node.company}</p>
                              <div className="detail-meta">
                                <span><FaCalendarAlt /> {formatDateLabel(node.startDate)} – {node.endDate ? formatDateLabel(node.endDate) : 'Present'}</span>
                                <span className="detail-duration">{getDurationMonthsYears(node.startDate, node.endDate)}</span>
                              </div>
                              {node.website && (
                                <a href={node.website} target="_blank" rel="noopener noreferrer" className="detail-link">
                                  <FaExternalLinkAlt /> Visit website
                                </a>
                              )}
                            </div>
                            <ul className="detail-list">
                              {(Array.isArray(node.description) ? node.description : []).map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <>
                            <div className="detail-header">
                              <h4>{node.name}</h4>
                              <div className="detail-meta">
                                <span><FaCalendarAlt /> {formatDateLabel(node.date)}</span>
                                {node.location && (
                                  <span><FaMapMarkerAlt /> {node.location}</span>
                                )}
                              </div>
                            </div>
                            {node.description && <p className="detail-desc">{node.description}</p>}
                            {node.url && (
                              <a href={node.url} target="_blank" rel="noopener noreferrer" className="detail-link">
                                <FaExternalLinkAlt /> Link
                              </a>
                            )}
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
